import type { DeclaredInputsSummary, JobRecord, JobSort } from './api';

// =============================================================================
// JOBS INBOX CACHE — W14 (docs/WALK-FINDINGS-2026-07-21.md), his spec
// verbatim: back-button to Jobs showed "All 0 · Loading your jobs…" —
// "these jobs must be read from the cache so the list is instant and only
// refreshed manually once logged in the first time." DIAGNOSIS: Jobs.tsx's
// useJobsInbox() re-initializes { jobs: [], loading: true } and re-fires
// GET /jobs on EVERY mount (react-router unmounts/remounts the page on each
// nav-away/nav-back — there is no route-level keep-alive) — a passive
// remount refetching from zero, not a user-requested refresh.
//
// THE FIX lives behind useJobsInbox.ts's own mount logic; this module is
// just the session-scoped store it reads/writes, deliberately kept OUT of
// that hook (a plain data module, no React) so it's directly unit-testable —
// this repo's vitest run has no jsdom (see useAuth.signOut.test.ts's own doc
// comment), so a hook can't be rendered/mounted in a test; a plain module
// can be exercised directly.
//
// TWO-TIER, mirroring driveExportState.ts's own sessionStorage discipline
// exactly (hasWindow()-style guard, try/catch, tab-scoped key prefix):
//   - an in-memory module variable — the fast path every mount within the
//     SAME page load reads, zero JSON parse, and (bonus, for free) exactly
//     what a HARD reload already clears (useAuth.ts's own R207 comment: "full
//     reload = every hook's in-memory state cleanly gone" — this app's own
//     established reasoning for why sign-out's hard navigation is enough for
//     in-memory-only state);
//   - sessionStorage as the backing store, surviving an ACTUAL page reload
//     within the same tab (a mobile back-nav some browsers choose to
//     re-launch the SPA shell for, not just an in-app router transition) —
//     the case R207's in-memory-only reasoning does NOT cover, and the exact
//     case his spec's "back-button" repro is closest to.
// Both are tab/session-scoped by construction — never localStorage, never
// shared across tabs or devices, never outliving the browser session.
//
// clearJobsInboxCache() is called from useAuth.ts's signOut — sessionStorage
// is NOT wiped by signOut's own hard navigation (unlike the in-memory tier),
// so without an explicit clear, a later sign-in on the SAME tab could
// synchronously hydrate from a PRIOR account's cached job list for one
// render before anything re-fetches. Explicit, not incidental.
// =============================================================================

export interface CachedJobsInbox {
  sort: JobSort;
  jobs: JobRecord[];
  declaredInputs: DeclaredInputsSummary;
  /** Epoch ms this entry was written — display/debug only. Deliberately NEVER
   *  used to silently auto-expire the cache: his spec is explicit that a
   *  refresh happens ONLY via the manual "Check for new jobs" action — a
   *  time-based expiry would reintroduce the exact passive-refetch-on-return
   *  behaviour this cache exists to remove. */
  cachedAt: number;
}

const STORAGE_KEY = 'tl-jobs-inbox-cache';

let memoryCache: CachedJobsInbox | null = null;

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function isValidEntry(value: unknown): value is CachedJobsInbox {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<CachedJobsInbox>;
  return (
    typeof v.sort === 'string' &&
    Array.isArray(v.jobs) &&
    typeof v.declaredInputs === 'object' &&
    v.declaredInputs !== null &&
    typeof v.cachedAt === 'number'
  );
}

/** Returns the cached inbox IFF one exists AND it was fetched under the
 *  EXACT sort the caller is asking for — a stale-sort cache is never
 *  silently served as if it matched (useJobsInbox.ts's mount always asks for
 *  its own initial sort state; a mismatch there falls back to a real fetch,
 *  same as a genuinely first-ever load). Promotes a sessionStorage hit to
 *  the in-memory tier so every LATER read this page load is the zero-parse
 *  fast path. */
export function readJobsInboxCache(sort: JobSort): CachedJobsInbox | null {
  if (memoryCache && memoryCache.sort === sort) return memoryCache;
  if (!hasSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEntry(parsed) || parsed.sort !== sort) return null;
    memoryCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

/** Writes BOTH tiers. Called after every successful GET /jobs result
 *  useJobsInbox.ts's refresh() receives — the initial first-load fetch AND a
 *  manual "Check for new jobs" poll's own follow-up refresh both flow
 *  through refresh(), so a single call site there keeps this cache honest:
 *  it always mirrors a real served response, never a client-side-only state
 *  change. */
export function writeJobsInboxCache(entry: Omit<CachedJobsInbox, 'cachedAt'>): void {
  const full: CachedJobsInbox = { ...entry, cachedAt: Date.now() };
  memoryCache = full;
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    // Best-effort only (private-browsing quota, storage disabled, etc.) —
    // the in-memory tier still serves this same page load either way.
  }
}

/** useAuth.ts's signOut — never let a later sign-in on the SAME tab hydrate
 *  from a prior account's jobs. */
export function clearJobsInboxCache(): void {
  memoryCache = null;
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort, same posture as every other write above
  }
}
